import { useEffect, useRef, useState, type DragEvent as ReactDragEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { attachmentsApi, cardsApi, landingsApi, templatesApi } from "../../api/endpoints";
import { ApiError } from "../../api/client";
import type { Attachment, Card as CardDto, CardStatus, CardType, Template } from "../../types/domain";
import type { DayLabeled } from "../../lib/recomputeDays";
import {
  IcoCheck,
  IcoClose,
  IcoCopy,
  IcoPlus,
  IcoTemplate,
  IcoTrash,
} from "../../components/icons";
import { ModalBackdrop } from "../../components/ModalBackdrop";
import { SaveAsTemplateDialog } from "./SaveAsTemplateDialog";
import { LandingPicker } from "./LandingPicker";
import { MdEditor } from "../../components/MdEditor";
import { mdToHtml } from "../../lib/md";
import { isTouchLike } from "../../lib/device";
import { useAnnounceCardPresence, useCardViewers } from "../../hooks/useCardPresence";
import { PresenceAvatars } from "../../components/PresenceAvatars";
import { ConflictDialog, type ConflictFieldDiff } from "../../components/ConflictDialog";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { usePermission } from "../../hooks/usePermission";
import { useCardPermissions } from "../../hooks/useCardPermissions";
import { useSession } from "../../hooks/useSession";
import {
  cardDraftsEqual,
  clearStoredCardDraft,
  createCardDraftKey,
  editCardDraftKey,
  loadStoredCardDraft,
  storeCardDraft,
  type CardDraft as Draft,
  type StoredCardDraft,
} from "./cardDraftStorage";

interface NewCardSeed {
  launchId: string;
  dayKey: string;
  typeId?: string;
}

interface CardModalProps {
  mode: "create" | "edit";
  card?: CardDto;
  seed?: NewCardSeed;
  types: CardType[];
  days: DayLabeled[];
  onClose: () => void;
}

interface PendingAttachment {
  id: string;
  file: File;
  objectUrl: string;
}

const STATUS_OPTS: Array<{ v: CardStatus; l: string }> = [
  { v: "draft", l: "Черновик" },
  { v: "ready", l: "Готово" },
  { v: "sent", l: "Отправлено" },
];

const toDraft = (card: CardDto): Draft => ({
  typeId: card.typeId,
  title: card.title,
  preview: card.preview,
  status: card.status,
  time: card.time ?? "",
  dayKey: card.dayKey,
  landingId: card.landingId ?? null,
});

// Письмо — самый частый тип карточки в типичном запуске. Используем его как
// стартовый, чтобы не переключать типизацию вручную каждый раз.
const DEFAULT_TYPE_ID = "t_email";

const statusLabel = (s: CardStatus): string =>
  STATUS_OPTS.find((x) => x.v === s)?.l ?? s;

function buildCardDiff(
  draft: Draft,
  server: CardDto,
  types: CardType[],
  days: DayLabeled[],
): ConflictFieldDiff[] {
  const out: ConflictFieldDiff[] = [];
  if (draft.title.trim() !== server.title) {
    out.push({ label: "Заголовок", yours: draft.title.trim(), theirs: server.title });
  }
  if ((draft.preview ?? "") !== (server.preview ?? "")) {
    out.push({
      label: "Содержимое",
      yours: draft.preview ?? "",
      theirs: server.preview ?? "",
    });
  }
  if (draft.status !== server.status) {
    out.push({
      label: "Статус",
      yours: statusLabel(draft.status),
      theirs: statusLabel(server.status),
    });
  }
  if ((draft.time ?? "") !== (server.time ?? "")) {
    out.push({
      label: "Время",
      yours: draft.time || "—",
      theirs: server.time || "—",
    });
  }
  if (draft.dayKey !== server.dayKey) {
    const day = (k: string) => {
      const d = days.find((x) => x.id === k);
      return d ? `${d.dayNum} · ${d.weekday} (${d.stageLabel})` : k;
    };
    out.push({ label: "День запуска", yours: day(draft.dayKey), theirs: day(server.dayKey) });
  }
  if (draft.typeId !== server.typeId) {
    const typeName = (id: string) => types.find((t) => t.id === id)?.name ?? id;
    out.push({ label: "Тип карточки", yours: typeName(draft.typeId), theirs: typeName(server.typeId) });
  }
  if ((draft.landingId ?? null) !== (server.landingId ?? null)) {
    out.push({
      label: "Лендинг",
      yours: draft.landingId ? draft.landingId : "—",
      theirs: server.landingId ? server.landingId : "—",
    });
  }
  return out;
}

const seedDraft = (seed: NewCardSeed, types: CardType[]): Draft => ({
  typeId:
    seed.typeId ??
    types.find((t) => t.id === DEFAULT_TYPE_ID)?.id ??
    types[0]?.id ??
    "",
  title: "",
  preview: "",
  status: "draft",
  time: "",
  dayKey: seed.dayKey,
  landingId: null,
});

export function CardModal({ mode, card, seed, types, days, onClose }: CardModalProps) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const session = useSession();
  const canCreateCard = usePermission("cards", "create");
  const templatePickerRef = useRef<HTMLDivElement | null>(null);
  const [createdCard, setCreatedCard] = useState<CardDto | null>(null);
  const existingCard = createdCard ?? card;
  // Точечная ownership-проверка на конкретной карточке: full-scope роли могут
  // редактировать всё, own-scope роли — только созданное ими.
  const cardPerms = useCardPermissions(existingCard);
  // Создаём — нужно cards.create. Редактируем существующую — нужно право на
  // конкретную карточку (cardPerms.canEdit).
  const isNew = mode === "create" && !existingCard;
  const readOnly = isNew ? !canCreateCard : !cardPerms.canEdit;
  const launchId = existingCard?.launchId ?? seed?.launchId ?? "";

  const [draftPersistence] = useState(() => {
    const ownerId = session.data?.id ?? "unknown";
    const key = card
      ? editCardDraftKey(ownerId, card.id)
      : createCardDraftKey(ownerId, seed!.launchId, seed!.dayKey);
    let stored = loadStoredCardDraft(key);
    if (stored) {
      const storedDraft = stored.draft;
      if (
        !types.some((type) => type.id === storedDraft.typeId) ||
        !days.some((day) => day.id === storedDraft.dayKey)
      ) {
        clearStoredCardDraft(key);
        stored = null;
      }
    }
    return {
      key,
      stored,
      staleStored:
        card && stored && stored.baselineUpdatedAt !== card.updatedAt ? stored : null,
      fallback: card ? toDraft(card) : seedDraft(seed!, types),
    };
  });
  const [draft, setDraft] = useState<Draft>(
    draftPersistence.staleStored
      ? draftPersistence.fallback
      : draftPersistence.stored?.draft ?? draftPersistence.fallback,
  );
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const pendingAttachmentsRef = useRef<PendingAttachment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [editorResetKey, setEditorResetKey] = useState(0);
  const [openingEditor, setOpeningEditor] = useState(false);
  // Baseline the card's updatedAt when the modal opens. PATCH requests carry
  // this as __expectedUpdatedAt so the server can reject stale writes with 409.
  // After a successful save we rebase to the server's returned updatedAt.
  const [baselineUpdatedAt, setBaselineUpdatedAt] = useState<string | null>(
    card
      ? draftPersistence.staleStored
        ? card.updatedAt
        : draftPersistence.stored?.baselineUpdatedAt ?? card.updatedAt
      : null,
  );
  const [staleStoredDraft, setStaleStoredDraft] = useState<StoredCardDraft | null>(
    draftPersistence.staleStored,
  );
  const [conflict, setConflict] = useState<CardDto | null>(null);

  pendingAttachmentsRef.current = pendingAttachments;

  useEffect(() => {
    if (readOnly) return;
    // Пока пользователь не выбрал версию, сохраняем локальный черновик нетронутым.
    // На экране при этом уже показывается более свежая серверная версия.
    if (staleStoredDraft) return;
    if (cardDraftsEqual(draft, draftPersistence.fallback)) {
      clearStoredCardDraft(draftPersistence.key);
      return;
    }
    storeCardDraft(draftPersistence.key, draft, baselineUpdatedAt);
  }, [baselineUpdatedAt, draft, draftPersistence, readOnly, staleStoredDraft]);

  useEffect(() => {
    return () => {
      for (const attachment of pendingAttachmentsRef.current) {
        URL.revokeObjectURL(attachment.objectUrl);
      }
    };
  }, []);

  const freshCardQuery = useQuery({
    queryKey: ["cards", launchId],
    queryFn: async () => (await cardsApi.list(launchId)).cards,
    select: (cards) =>
      existingCard ? cards.find((c) => c.id === existingCard.id) ?? existingCard : undefined,
    enabled: !isNew && !!existingCard,
  });
  const freshCard = freshCardQuery.data ?? existingCard;
  const attachments = freshCard?.attachments ?? [];

  const landingsQuery = useQuery({
    queryKey: ["landings"],
    queryFn: async () => (await landingsApi.list()).landings,
  });
  const templatesQuery = useQuery({
    queryKey: ["templates", "card-modal"],
    queryFn: async () => (await templatesApi.list()).templates,
    enabled: !readOnly,
  });
  const allLandings = landingsQuery.data ?? [];
  const currentDay = days.find((d) => d.id === draft.dayKey);
  const templates = (templatesQuery.data ?? []).slice().sort((a, b) => {
    if (currentDay && a.stage !== b.stage) {
      if (a.stage === currentDay.stage) return -1;
      if (b.stage === currentDay.stage) return 1;
    }
    return a.name.localeCompare(b.name, "ru");
  });
  const linkedLanding = draft.landingId
    ? allLandings.find((l) => l.id === draft.landingId) ?? null
    : null;

  useEffect(() => {
    if (!templatePickerOpen) return;
    const close = (e: MouseEvent) => {
      if (!templatePickerRef.current?.contains(e.target as Node)) {
        setTemplatePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [templatePickerOpen]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["cards", launchId] });

  // Announce presence only on existing cards; the "create" modal doesn't have
  // an id yet, so there's nothing for peers to subscribe to.
  useAnnounceCardPresence(!isNew && existingCard ? existingCard.id : null);
  const viewers = useCardViewers(launchId, !isNew && existingCard ? existingCard.id : null);

  const createMutation = useMutation({
    mutationFn: async () => {
      const result = await cardsApi.create({
        launchId,
        dayKey: draft.dayKey,
        typeId: draft.typeId,
        title: draft.title.trim(),
        preview: draft.preview,
        status: draft.status,
        time: draft.time || null,
        landingId: draft.landingId,
        fromTemplateId: null,
      });
      const files = pendingAttachmentsRef.current.map((attachment) => attachment.file);
      if (files.length > 0) {
        try {
          await uploadImageFilesToCard(result.card.id, files);
        } catch (err) {
          setCreatedCard(result.card);
          setBaselineUpdatedAt(result.card.updatedAt);
          throw err;
        }
      }
      return result;
    },
    onSuccess: () => {
      clearStoredCardDraft(draftPersistence.key);
      invalidate();
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  const patchMutation = useMutation({
    mutationFn: (opts?: { force?: boolean }) =>
      cardsApi.patch(
        existingCard!.id,
        {
          dayKey: draft.dayKey,
          typeId: draft.typeId,
          title: draft.title.trim(),
          preview: draft.preview,
          status: draft.status,
          time: draft.time || null,
          landingId: draft.landingId,
        },
        opts?.force || !baselineUpdatedAt
          ? undefined
          : { expectedUpdatedAt: baselineUpdatedAt },
      ),
    onSuccess: () => {
      clearStoredCardDraft(draftPersistence.key);
      invalidate();
      onClose();
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.status === 409) {
        const current = (err.details as { current?: CardDto } | undefined)?.current;
        if (current) {
          setConflict(current);
          return;
        }
      }
      setError(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => cardsApi.remove(existingCard!.id),
    onSuccess: () => {
      clearStoredCardDraft(draftPersistence.key);
      invalidate();
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  const duplicateMutation = useMutation({
    mutationFn: () => cardsApi.duplicate(existingCard!.id),
    onSuccess: () => {
      clearStoredCardDraft(draftPersistence.key);
      invalidate();
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  const activeType = types.find((t) => t.id === draft.typeId) ?? types[0];
  const isLanding = activeType?.kind === "landing";
  const canSave = draft.title.trim().length > 0 && draft.typeId && draft.dayKey;
  const busy =
    createMutation.isPending ||
    patchMutation.isPending ||
    deleteMutation.isPending ||
    duplicateMutation.isPending ||
    openingEditor;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = () => {
    if (!canSave || busy) return;
    setError(null);
    if (isNew) createMutation.mutate();
    else patchMutation.mutate(undefined);
  };

  const handleConflictKeepMine = () => {
    patchMutation.mutate({ force: true });
    setConflict(null);
  };
  const handleConflictTakeTheirs = () => {
    if (!conflict) return;
    setDraft(toDraft(conflict));
    setBaselineUpdatedAt(conflict.updatedAt);
    setConflict(null);
  };

  const handleOpenLatestServerDraft = () => {
    clearStoredCardDraft(draftPersistence.key);
    setDraft(draftPersistence.fallback);
    setBaselineUpdatedAt(card?.updatedAt ?? null);
    setStaleStoredDraft(null);
    setEditorResetKey((n) => n + 1);
  };

  const handleKeepStoredDraft = () => {
    if (!staleStoredDraft) return;
    setDraft(staleStoredDraft.draft);
    setBaselineUpdatedAt(staleStoredDraft.baselineUpdatedAt);
    setStaleStoredDraft(null);
    setEditorResetKey((n) => n + 1);
  };

  const handleDelete = () => {
    if (!existingCard || busy) return;
    const yes = window.confirm(`Удалить карточку «${existingCard.title}»?`);
    if (yes) deleteMutation.mutate();
  };

  const handleDuplicate = () => {
    if (!existingCard || busy) return;
    duplicateMutation.mutate();
  };

  const appendPendingAttachments = (files: File[]) => {
    setPendingAttachments((current) => [
      ...current,
      ...files.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        file,
        objectUrl: URL.createObjectURL(file),
      })),
    ]);
  };

  const removePendingAttachment = (id: string) => {
    setPendingAttachments((current) => {
      const removed = current.find((attachment) => attachment.id === id);
      if (removed) URL.revokeObjectURL(removed.objectUrl);
      return current.filter((attachment) => attachment.id !== id);
    });
  };

  const applyTemplateToDraft = (template: Template) => {
    const nextTypeId = types.some((t) => t.id === template.typeId)
      ? template.typeId
      : draft.typeId;
    const nextType = types.find((t) => t.id === nextTypeId);
    setDraft((d) => ({
      ...d,
      typeId: nextTypeId,
      title: (template.subject || template.name).trim(),
      preview: template.preview ?? "",
      time: template.time ?? "",
      landingId: nextType?.kind === "landing" ? d.landingId : null,
    }));
    setEditorResetKey((n) => n + 1);
    setTemplatePickerOpen(false);
  };

  const createLanding = async (titleSeed: string): Promise<string> => {
    const seedTitle = (titleSeed || draft.title || "Новый лендинг").trim() || "Новый лендинг";
    const { landing } = await landingsApi.create({
      title: seedTitle,
      status: "draft",
      content: "",
      launchId: launchId || null,
    });
    qc.invalidateQueries({ queryKey: ["landings"] });
    setDraft((d) => ({ ...d, landingId: landing.id, preview: "" }));
    return landing.id;
  };

  const openLandingEditor = async (landingId: string) => {
    if (busy) return;
    setError(null);
    setOpeningEditor(true);
    try {
      if (isNew) {
        if (!draft.title.trim()) {
          setError("Введите заголовок карточки перед открытием редактора.");
          return;
        }
        await cardsApi.create({
          launchId,
          dayKey: draft.dayKey,
          typeId: draft.typeId,
          title: draft.title.trim(),
          preview: draft.preview,
          status: draft.status,
          time: draft.time || null,
          landingId,
          fromTemplateId: null,
        });
      } else if (existingCard) {
        await cardsApi.patch(existingCard.id, {
          dayKey: draft.dayKey,
          typeId: draft.typeId,
          title: draft.title.trim(),
          preview: draft.preview,
          status: draft.status,
          time: draft.time || null,
          landingId,
        });
      }
      await qc.invalidateQueries({ queryKey: ["cards", launchId] });
      clearStoredCardDraft(draftPersistence.key);
      onClose();
      navigate(`/landings/${landingId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setOpeningEditor(false);
    }
  };

  if (!activeType) {
    return (
      <ModalBackdrop onClose={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-body">Нет доступных типов карточек.</div>
        </div>
      </ModalBackdrop>
    );
  }

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span
            className="type-badge"
            style={
              {
                "--type-color": activeType.color,
                "--type-bg": activeType.bg,
                "--type-text": activeType.text,
              } as React.CSSProperties
            }
          >
            <span className="type-dot" />
            {activeType.name}
          </span>
          <h2>{isNew ? "Новая карточка" : readOnly ? "Карточка" : "Редактирование карточки"}</h2>
          <div className="spacer" />
          {viewers.length > 0 && <PresenceAvatars viewers={viewers} />}
          <button type="button" className="modal-close" onClick={onClose}>
            <IcoClose size={16} />
          </button>
        </div>

        <div className="modal-body">
          {!readOnly && (
            <div className="form-row form-row--tight">
              <div className="type-picker">
                {types.map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    className={"type-option" + (draft.typeId === t.id ? " active" : "")}
                    style={
                      {
                        "--type-color": t.color,
                        "--type-bg": t.bg,
                        "--type-text": t.text,
                      } as React.CSSProperties
                    }
                    onClick={() => setDraft({ ...draft, typeId: t.id })}
                  >
                    <span className="type-dot" style={{ background: t.color }} />
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="form-row form-row--tight">
            <div className="card-title-row">
              <input
                className="form-input form-input--title"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Заголовок карточки"
                autoFocus={!readOnly && !isTouchLike()}
                disabled={readOnly}
              />
              {!readOnly && (
                <div className="template-picker-wrap" ref={templatePickerRef}>
                  <button
                    type="button"
                    className="btn-ghost card-template-btn"
                    onClick={() => setTemplatePickerOpen((v) => !v)}
                    disabled={busy}
                  >
                    <IcoTemplate size={13} /> Шаблон
                  </button>
                  {templatePickerOpen && (
                    <div className="card-template-popover">
                      <div className="apply-popover-head">
                        <span>Выберите шаблон</span>
                        <button
                          type="button"
                          className="modal-close"
                          onClick={() => setTemplatePickerOpen(false)}
                          aria-label="Закрыть"
                        >
                          <IcoClose size={13} />
                        </button>
                      </div>
                      <div className="card-template-list">
                        {templatesQuery.isLoading ? (
                          <div className="card-template-empty">Загружаем шаблоны...</div>
                        ) : templates.length === 0 ? (
                          <div className="card-template-empty">Шаблонов пока нет.</div>
                        ) : (
                          templates.map((tpl) => {
                            const type = types.find((t) => t.id === tpl.typeId);
                            const sameStage = currentDay?.stage === tpl.stage;
                            return (
                              <button
                                type="button"
                                key={tpl.id}
                                className="card-template-item"
                                onClick={() => applyTemplateToDraft(tpl)}
                              >
                                <span className="card-template-title">{tpl.name}</span>
                                <span className="card-template-meta">
                                  {type?.name ?? "Тип не найден"}
                                  {sameStage ? " · этот этап" : ""}
                                  {tpl.time ? ` · ${tpl.time}` : ""}
                                </span>
                                {tpl.subject && (
                                  <span className="card-template-subject">{tpl.subject}</span>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-row form-row--tight split split-compact">
            <div>
              <label className="form-label">День запуска</label>
              <select
                className="form-select"
                value={draft.dayKey}
                onChange={(e) => setDraft({ ...draft, dayKey: e.target.value })}
                disabled={readOnly}
              >
                {days.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.weekday} · {d.date} · D{d.dayNum}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Время</label>
              <input
                className="form-input"
                type="time"
                value={draft.time}
                onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                disabled={readOnly}
              />
            </div>
          </div>

          {isLanding ? (
            <div className="form-row form-row--separated">
              <label className="form-label">Контент лендинга</label>
              <LandingPicker
                landing={linkedLanding}
                allLandings={allLandings}
                activeLaunchId={launchId}
                draftTitle={draft.title}
                onLink={(id) => setDraft((d) => ({ ...d, landingId: id, preview: "" }))}
                onUnlink={() => setDraft((d) => ({ ...d, landingId: null }))}
                onCreateLanding={createLanding}
                onOpenEditor={openLandingEditor}
                disabled={busy || readOnly}
                readOnly={readOnly}
              />
            </div>
          ) : (
            <div className="form-row form-row--separated">
              <label className="form-label">
                {activeType.id === "t_note"
                  ? "Заметка"
                  : activeType.id === "t_email"
                    ? "Текст письма / сообщения"
                    : "Текст"}
              </label>
              {readOnly ? (
                <div
                  className="md-readonly-preview"
                  dangerouslySetInnerHTML={{ __html: mdToHtml(draft.preview || "") as string }}
                />
              ) : (
                <MdEditor
                  key={`md-${activeType.id}-${existingCard?.id ?? "new"}-${editorResetKey}`}
                  value={draft.preview}
                  onChange={(md) => setDraft((d) => ({ ...d, preview: md }))}
                  className="md-editor--compact"
                  enableFocusMode
                  focusTitle={draft.title || activeType.name}
                />
              )}
            </div>
          )}

          <div className="form-row">
            <label className="form-label">
              Вложения{" "}
              <span
                style={{
                  fontWeight: 400,
                  textTransform: "none",
                  letterSpacing: 0,
                  opacity: 0.7,
                }}
              >
                (изображения до 20 MB)
              </span>
            </label>
            {isNew ? (
              <PendingAttachmentSection
                attachments={pendingAttachments}
                onAdd={appendPendingAttachments}
                onRemove={removePendingAttachment}
                readOnly={readOnly}
                saving={createMutation.isPending}
              />
            ) : existingCard ? (
              <AttachmentSection
                cardId={existingCard.id}
                launchId={launchId}
                attachments={attachments}
                readOnly={readOnly}
              />
            ) : null}
          </div>

          <div className="form-row">
            <label className="form-label">Статус</label>
            <div className="status-picker">
              {STATUS_OPTS.map((s) => (
                <button
                  type="button"
                  key={s.v}
                  className={"status-option" + (draft.status === s.v ? " active" : "")}
                  onClick={() => !readOnly && setDraft({ ...draft, status: s.v })}
                  disabled={readOnly}
                >
                  {s.v === "ready" && <IcoCheck size={12} />}
                  {s.l}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div
              style={{
                color: "var(--text-danger, #DC2626)",
                fontSize: 12.5,
                marginTop: 8,
              }}
            >
              {error}
            </div>
          )}
        </div>

        <div className="modal-foot">
          {!isNew && existingCard && !readOnly && (
            <>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDelete}
                disabled={busy}
              >
                <IcoTrash size={13} /> Удалить
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={handleDuplicate}
                disabled={busy}
              >
                <IcoCopy size={13} /> Дублировать
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setSaveTplOpen(true)}
                disabled={busy || !draft.title.trim()}
                title="Сохранить карточку как шаблон для повторного использования"
              >
                <IcoTemplate size={13} /> В шаблон
              </button>
            </>
          )}
          <div className="spacer" />
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
            {readOnly ? "Закрыть" : "Отмена"}
          </button>
          {!readOnly && (
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={!canSave || busy}
            >
              <IcoCheck size={14} /> Сохранить
            </button>
          )}
        </div>
      </div>
      {saveTplOpen && existingCard && (
        <SaveAsTemplateDialog
          seed={{
            typeId: draft.typeId,
            title: draft.title.trim(),
            preview: draft.preview,
            time: draft.time,
          }}
          defaultStage={days.find((d) => d.id === draft.dayKey)?.stage ?? "warmup"}
          onClose={() => setSaveTplOpen(false)}
        />
      )}
      {staleStoredDraft && (
        <ConfirmDialog
          title="На сервере есть более новая версия"
          message="Пока этот черновик хранился в браузере, письмо изменил другой пользователь. Какую версию открыть?"
          confirmLabel="Открыть новую"
          cancelLabel="Оставить мой черновик"
          danger={false}
          onConfirm={handleOpenLatestServerDraft}
          onClose={handleKeepStoredDraft}
        />
      )}
      {conflict && (
        <ConflictDialog
          title="Карточка была изменена"
          updatedAt={conflict.updatedAt}
          fields={buildCardDiff(draft, conflict, types, days)}
          onKeepMine={handleConflictKeepMine}
          onTakeTheirs={handleConflictTakeTheirs}
          onClose={() => setConflict(null)}
          busy={patchMutation.isPending}
        />
      )}
    </ModalBackdrop>
  );
}

const ALLOWED_MIME = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "image/svg+xml",
]);
const MAX_SIZE = 20 * 1024 * 1024;

function validateImageFile(file: File) {
  if (!ALLOWED_MIME.has(file.type)) {
    throw new Error(`Формат «${file.type || file.name}» не поддерживается (png/jpeg/webp/gif/svg)`);
  }
  if (file.size > MAX_SIZE) {
    throw new Error(`«${file.name}» больше 20 MB`);
  }
}

function collectClipboardImageFiles(data: DataTransfer): File[] {
  const imageFiles: File[] = [];
  for (const item of Array.from(data.items)) {
    if (item.kind !== "file" || !item.type.startsWith("image/")) continue;
    const file = item.getAsFile();
    if (!file) continue;
    const ext =
      file.type === "image/png" ? "png"
      : file.type === "image/jpeg" ? "jpg"
      : file.type === "image/webp" ? "webp"
      : file.type === "image/gif" ? "gif"
      : file.type === "image/svg+xml" ? "svg"
      : (file.name.split(".").pop() ?? "bin");
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    imageFiles.push(new File([file], `pasted-${ts}.${ext}`, { type: file.type }));
  }
  return imageFiles;
}

async function uploadImageFileToCard(cardId: string, file: File) {
  validateImageFile(file);
  const { uploadUrl, storageKey } = await attachmentsApi.presignUpload({
    cardId,
    filename: file.name,
    mimeType: file.type,
    size: file.size,
  });
  const put = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!put.ok) throw new Error(`Не удалось загрузить ${file.name}: ${put.status}`);
  await attachmentsApi.commit({
    cardId,
    storageKey,
    filename: file.name,
    mimeType: file.type,
    size: file.size,
  });
}

async function uploadImageFilesToCard(cardId: string, files: File[]) {
  for (const file of files) {
    await uploadImageFileToCard(cardId, file);
  }
}

const formatSize = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

interface AttachmentSectionProps {
  cardId: string;
  launchId: string;
  attachments: Attachment[];
  readOnly?: boolean;
}

interface PendingAttachmentSectionProps {
  attachments: PendingAttachment[];
  onAdd: (files: File[]) => void;
  onRemove: (id: string) => void;
  readOnly?: boolean;
  saving?: boolean;
}

function PendingAttachmentSection({
  attachments,
  onAdd,
  onRemove,
  readOnly = false,
  saving = false,
}: PendingAttachmentSectionProps) {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);

  const handleFiles = (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    setUploadError(null);
    try {
      const next = Array.from(files);
      for (const file of next) validateImageFile(file);
      onAdd(next);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleFilesRef = useRef(handleFiles);
  handleFilesRef.current = handleFiles;

  useEffect(() => {
    if (readOnly) return;
    const onPaste = (e: ClipboardEvent) => {
      if (!e.clipboardData) return;
      const target = e.target as HTMLElement | null;
      if (target?.isContentEditable) return;
      const imageFiles = collectClipboardImageFiles(e.clipboardData);
      if (imageFiles.length === 0) return;
      e.preventDefault();
      handleFilesRef.current(imageFiles);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [readOnly]);

  const hasFilesInDrag = (e: ReactDragEvent) =>
    Array.from(e.dataTransfer.types ?? []).includes("Files");

  const onDragEnter = (e: ReactDragEvent) => {
    if (readOnly || !hasFilesInDrag(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  };
  const onDragOver = (e: ReactDragEvent) => {
    if (readOnly || !hasFilesInDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = (e: ReactDragEvent) => {
    if (readOnly || !hasFilesInDrag(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const onDrop = (e: ReactDragEvent) => {
    if (readOnly || !hasFilesInDrag(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter((file) => file.type.startsWith("image/"));
    if (files.length === 0) {
      setUploadError("Можно перетаскивать только изображения (png/jpeg/webp/gif/svg)");
      return;
    }
    handleFiles(files);
  };

  return (
    <div
      className={"attachments" + (dragOver ? " drag-over" : "")}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {attachments.map((attachment) => (
        <div className="attach-row" key={attachment.id}>
          <div
            className="attach-thumb"
            style={{ backgroundImage: `url(${attachment.objectUrl})` }}
          />
          <div className="attach-meta">
            <b>{attachment.file.name}</b>
            <span>{formatSize(attachment.file.size)} · загрузится после сохранения</span>
          </div>
          {!readOnly && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => onRemove(attachment.id)}
              disabled={saving}
              title="Убрать"
            >
              <IcoTrash size={13} />
            </button>
          )}
        </div>
      ))}
      {!readOnly && (
        <label className="attach-add" style={{ opacity: saving ? 0.6 : 1 }}>
          <IcoPlus size={13} />
          {saving ? "Загружаем..." : "Прикрепить изображение"}
          {!saving && (
            <span style={{ color: "var(--text-3)", fontSize: 11, marginLeft: 6 }}>
              или Ctrl+V
            </span>
          )}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            multiple
            style={{ display: "none" }}
            disabled={saving}
            onChange={(e) => {
              const files = e.target.files;
              handleFiles(files);
              e.target.value = "";
            }}
          />
        </label>
      )}
      {uploadError && (
        <div style={{ color: "var(--text-danger, #DC2626)", fontSize: 12 }}>{uploadError}</div>
      )}
    </div>
  );
}

function AttachmentSection({ cardId, launchId, attachments, readOnly = false }: AttachmentSectionProps) {
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["cards", launchId] });

  const removeMutation = useMutation({
    mutationFn: (id: string) => attachmentsApi.remove(id),
    onSuccess: invalidate,
  });

  const handleFiles = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    setUploadError(null);
    setUploading(true);
    try {
      await uploadImageFilesToCard(cardId, Array.from(files));
      await invalidate();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const handleFilesRef = useRef(handleFiles);
  handleFilesRef.current = handleFiles;

  useEffect(() => {
    if (readOnly) return;
    const onPaste = (e: ClipboardEvent) => {
      if (!e.clipboardData) return;
      const target = e.target as HTMLElement | null;
      if (target?.isContentEditable) return;
      const imageFiles = collectClipboardImageFiles(e.clipboardData);
      if (imageFiles.length === 0) return;
      e.preventDefault();
      handleFilesRef.current(imageFiles);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [readOnly]);

  const hasFilesInDrag = (e: ReactDragEvent) =>
    Array.from(e.dataTransfer.types ?? []).includes("Files");

  const onDragEnter = (e: ReactDragEvent) => {
    if (readOnly || !hasFilesInDrag(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  };
  const onDragOver = (e: ReactDragEvent) => {
    if (readOnly || !hasFilesInDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = (e: ReactDragEvent) => {
    if (readOnly || !hasFilesInDrag(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const onDrop = (e: ReactDragEvent) => {
    if (readOnly || !hasFilesInDrag(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) {
      setUploadError("Можно перетаскивать только изображения (png/jpeg/webp/gif/svg)");
      return;
    }
    handleFiles(files);
  };

  return (
    <div
      className={"attachments" + (dragOver ? " drag-over" : "")}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {attachments.map((a) => (
        <AttachmentRow
          key={a.id}
          attachment={a}
          onDelete={() => removeMutation.mutate(a.id)}
          deleting={removeMutation.isPending && removeMutation.variables === a.id}
          readOnly={readOnly}
        />
      ))}
      {!readOnly && (
        <label className="attach-add" style={{ opacity: uploading ? 0.6 : 1 }}>
          <IcoPlus size={13} />
          {uploading ? "Загружаем…" : "Прикрепить изображение"}
          {!uploading && (
            <span style={{ color: "var(--text-3)", fontSize: 11, marginLeft: 6 }}>
              или Ctrl+V
            </span>
          )}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            multiple
            style={{ display: "none" }}
            disabled={uploading}
            onChange={(e) => {
              const files = e.target.files;
              handleFiles(files);
              e.target.value = "";
            }}
          />
        </label>
      )}
      {uploadError && (
        <div style={{ color: "var(--text-danger, #DC2626)", fontSize: 12 }}>{uploadError}</div>
      )}
    </div>
  );
}

interface AttachmentRowProps {
  attachment: Attachment;
  onDelete: () => void;
  deleting: boolean;
  readOnly?: boolean;
}

function AttachmentRow({ attachment, onDelete, deleting, readOnly = false }: AttachmentRowProps) {
  const urlQuery = useQuery({
    queryKey: ["attachment-url", attachment.id],
    queryFn: async () => (await attachmentsApi.downloadUrl(attachment.id)).url,
    staleTime: 10 * 60 * 1000,
  });
  const url = urlQuery.data;
  const [previewOpen, setPreviewOpen] = useState(false);

  const isImage = attachment.mimeType.startsWith("image/");
  const canPreview = isImage && !!url;

  return (
    <div className="attach-row">
      <div
        className="attach-thumb"
        style={{
          ...(url ? { backgroundImage: `url(${url})` } : {}),
          cursor: canPreview ? "zoom-in" : undefined,
        }}
        onClick={canPreview ? () => setPreviewOpen(true) : undefined}
        title={canPreview ? "Открыть превью" : undefined}
      />
      {previewOpen && url && (
        <AttachmentPreview
          url={url}
          filename={attachment.filename}
          onClose={() => setPreviewOpen(false)}
        />
      )}
      <div className="attach-meta">
        <b>{attachment.filename}</b>
        <span>{formatSize(attachment.size)}</span>
      </div>
      {url && (
        <a
          href={url}
          download={attachment.filename}
          target="_blank"
          rel="noopener noreferrer"
          className="icon-btn"
          title="Скачать"
          onClick={(e) => e.stopPropagation()}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="M7 10l5 5 5-5" />
            <path d="M12 15V3" />
          </svg>
        </a>
      )}
      {!readOnly && (
        <button
          type="button"
          className="icon-btn"
          onClick={onDelete}
          disabled={deleting}
          title="Удалить"
        >
          <IcoTrash size={13} />
        </button>
      )}
    </div>
  );
}

interface AttachmentPreviewProps {
  url: string;
  filename: string;
  onClose: () => void;
}

function AttachmentPreview({ url, filename, onClose }: AttachmentPreviewProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.82)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 300,
        padding: 40,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          maxWidth: "90vw",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
        }}
      >
        <img
          src={url}
          alt={filename}
          style={{
            maxWidth: "90vw",
            maxHeight: "82vh",
            objectFit: "contain",
            borderRadius: 8,
            boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
            background: "var(--surface)",
          }}
        />
        <div
          style={{
            color: "rgba(255,255,255,0.85)",
            fontSize: 12.5,
            fontFamily: "JetBrains Mono, monospace",
            textShadow: "0 1px 2px rgba(0,0,0,0.4)",
          }}
        >
          {filename}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть"
          style={{
            position: "absolute",
            top: -14,
            right: -14,
            width: 36,
            height: 36,
            borderRadius: 999,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
            color: "var(--text)",
          }}
        >
          <IcoClose size={16} />
        </button>
      </div>
    </div>
  );
}
